/* eslint-disable react/no-unknown-property */
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tab,
  Tabs
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import styled from "styled-components";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { todoListRescheduleTask } from "./todo_list_util";
import SeenCheckbox from "../seen_checkbox";
import { START_HOURS, shiftFromWallClock, shiftNumberForHour, succ } from "../../shift";

const hourOptions = Array.from(Array(24).keys()).map((hour) => hour.toString().padStart(2, "0"));
const minuteOptions = Array.from(Array(60).keys()).map((minute) =>
  minute.toString().padStart(2, "0")
);

const formatDateForDisplay = (date) => {
  if (!date) return "";
  let d;
  if (date instanceof Date) {
    d = date;
  } else if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split("-");
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(date);
  }
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  });
};

const formatTimeForDisplay = (hour, minute) => {
  const h = parseInt(hour, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minute} ${ampm}`;
};

const shiftNumberToLabel = (shiftNum) => {
  if (shiftNum === 1 || shiftNum === "1") return "First Shift (7:00 AM)";
  if (shiftNum === 2 || shiftNum === "2") return "Second Shift (3:00 PM)";
  if (shiftNum === 3 || shiftNum === "3") return "Third Shift (11:00 PM)";
  return "Original Time";
};

const getShiftFromTime = (hour) => {
  // Shift boundaries all fall on the hour, so the minute is not needed.
  return shiftNumberForHour(parseInt(hour, 10));
};

const timeToShiftNumber = (timeToConvertToShiftNumber) => {
  return shiftNumberForHour(timeToConvertToShiftNumber.getHours());
};

const turnStringToDateIgnoringTimeZone = (dateString) => {
  const splitDateString = dateString.split("-");
  return new Date(splitDateString[0], splitDateString[1] - 1, splitDateString[2]);
};

const combineDateAndTime = (dateString, time) => {
  const date = turnStringToDateIgnoringTimeZone(dateString);
  // Extract hours, minutes, seconds, and milliseconds from the time Date object
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  const milliseconds = time.getMilliseconds();

  // Set the time components to the date object
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);
  date.setMilliseconds(milliseconds);
  return date;
};

export const ReScheduleButton = (props) => {
  const {
    toDoListTitle,
    shiftNumber,
    todoListId,
    todoListDate,
    todoListTime,
    hasAppointmentRequest,
    matchedPeerName
  } = props;

  const [modalOpen, setModalOpen] = useState(false);

  const onClick = () => {
    setModalOpen(true);
  };

  return (
    <>
      <TODOListActionButtonReScheduleModal
        modalOpen={modalOpen}
        handleClose={() => setModalOpen(false)}
        shiftNumber={shiftNumber}
        todoListId={todoListId}
        todoListDate={todoListDate}
        todoListTime={todoListTime}
        hasAppointmentRequest={hasAppointmentRequest}
        matchedPeerName={matchedPeerName}
      />
      <button
        explorator_to_do_list_test_id_title={`to_do_list_action_button_reschedule_${toDoListTitle}`}
        explorator_test_id={`to_do_list_action_button_reschedule`}
        className={
          "button ccw-button to-do-list-action-button-base to-do-list-skip-occurrence-button todo-action-button todo-action-button--reschedule"
        }
        onClick={onClick}
      >
        <i className={"far fa-clock"} aria-hidden="true"></i>
        <span>Re-Schedule </span>
      </button>
    </>
  );
};

ReScheduleButton.propTypes = {
  todoListId: PropTypes.string,
  todoListDate: PropTypes.string,
  time: PropTypes.string,
  type: PropTypes.string, // can be: complete, incomplete, skip, inprogress
  isComplete: PropTypes.bool,
  isInComplete: PropTypes.bool,
  shiftNumber: PropTypes.number,
  toDoListTitle: PropTypes.string,
  todoListTime: PropTypes.object,
  hasAppointmentRequest: PropTypes.bool,
  matchedPeerName: PropTypes.string
};

const TODOListActionButtonReScheduleModal = ({
  todoListDate,
  todoListTime,
  handleClose,
  modalOpen,
  todoListId,
  shiftNumber,
  hasAppointmentRequest,
  matchedPeerName
}) => {
  const [reason, setReason] = useState();
  const [initials, setInitials] = useState();
  const [isSeen, setIsSeen] = useState();
  const [keepMatching, setKeepMatching] = useState(false);

  const [shiftNumForward, setShiftNumForward] = useState(1);
  const [shiftNumCustom, setShiftNumCustom] = useState("1");

  const [dateToRescheduleTo, setDateToRescheduleTo] = useState();

  // Custom time state
  const [customHour, setCustomHour] = useState("09");
  const [customMinute, setCustomMinute] = useState("00");

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingRescheduleData, setPendingRescheduleData] = useState(null);

  const checkError = () => {
    if (!reason || !initials) {
      toastr.error("Error Please fill out both initials and reason.");
      return true;
    }
    return false;
  };

  const handleConfirmReschedule = async () => {
    if (!pendingRescheduleData) return;

    const { dateToReschedule, shiftForRequest, keepOriginalTime, newShiftTime, customTimeValue } =
      pendingRescheduleData;

    const res = await todoListRescheduleTask(
      todoListId,
      turnStringToDateIgnoringTimeZone(todoListDate),
      newShiftTime || dateToReschedule,
      shiftForRequest,
      reason,
      initials,
      isSeen,
      keepOriginalTime,
      customTimeValue,
      keepMatching
    );

    if (res && res.error) {
      setShowConfirmation(false);
      return;
    }

    const redirectDate = res?.data?.redirect_date;

    setShowConfirmation(false);
    handleClose();

    if (redirectDate) {
      sessionStorage.setItem(
        "pendingToast",
        JSON.stringify({
          message: `Task rescheduled to ${pendingRescheduleData.displayDate}. Click here to view.`,
          type: "success",
          redirectUrl: `/to_do_lists/index_search?to_do_date=${redirectDate}`
        })
      );
    }

    window.location.reload();
  };

  const onConfirmCustomDate = async () => {
    if (checkError()) return;
    if (!dateToRescheduleTo) {
      toastr.error("Please select a date to reschedule to.");
      return;
    }

    const keepOriginalTime = shiftNumCustom === "keep_original";
    const isCustomTime = shiftNumCustom === "custom";

    let shiftForRequest;
    let displayShift;
    let customTimeValue = null;

    if (isCustomTime) {
      shiftForRequest = getShiftFromTime(customHour);
      displayShift = `Custom Time: ${formatTimeForDisplay(customHour, customMinute)}`;
      customTimeValue = `${customHour}:${customMinute}`;
    } else if (keepOriginalTime) {
      shiftForRequest = shiftNumber;
      displayShift = "Keeping original time";
    } else {
      shiftForRequest = Number(shiftNumCustom);
      displayShift = shiftNumberToLabel(shiftForRequest);
    }

    setPendingRescheduleData({
      dateToReschedule: dateToRescheduleTo,
      shiftForRequest,
      keepOriginalTime,
      newShiftTime: null,
      customTimeValue,
      displayDate: formatDateForDisplay(dateToRescheduleTo),
      displayShift
    });
    setShowConfirmation(true);
  };

  const moveShiftForwardOne = (currentTime) => {
    const newDate = combineDateAndTime(todoListDate, new Date(currentTime));

    // Classify the wall-clock time into its shift, then jump to the start of
    // the next shift (3rd shift rolls over to the 1st shift of the next date).
    const nextShift = succ(
      shiftFromWallClock(
        newDate.getFullYear(),
        newDate.getMonth() + 1,
        newDate.getDate(),
        newDate.getHours()
      )
    );
    const [year, month, day] = nextShift.date.split("-").map(Number);
    newDate.setFullYear(year, month - 1, day);
    newDate.setHours(START_HOURS[nextShift.number], 0, 0, 0);

    return newDate;
  };

  const onConfirmMoveForward = async () => {
    if (checkError()) return;

    let newShiftTime = combineDateAndTime(todoListDate, new Date(todoListTime));
    for (
      let shiftNumForwardIndex = 1;
      shiftNumForwardIndex <= shiftNumForward;
      shiftNumForwardIndex++
    ) {
      newShiftTime = moveShiftForwardOne(newShiftTime);
    }

    const shiftForRequest = timeToShiftNumber(newShiftTime);
    const dateString = `${newShiftTime.getFullYear()}-${String(
      newShiftTime.getMonth() + 1
    ).padStart(2, "0")}-${String(newShiftTime.getDate()).padStart(2, "0")}`;

    setPendingRescheduleData({
      dateToReschedule: dateString,
      shiftForRequest,
      keepOriginalTime: false,
      newShiftTime,
      customTimeValue: null,
      displayDate: formatDateForDisplay(newShiftTime),
      displayShift: shiftNumberToLabel(shiftForRequest)
    });
    setShowConfirmation(true);
  };

  if (!modalOpen) return <></>;

  if (showConfirmation && pendingRescheduleData) {
    const isMatched = hasAppointmentRequest && matchedPeerName;

    return (
      <Dialog open={true} onClose={() => setShowConfirmation(false)}>
        <DialogContent className="to-do-completed-popup">
          <ConfirmationContent>
            <ConfirmationTitle>Confirm Reschedule</ConfirmationTitle>
            <p>Reschedule task to:</p>
            <ConfirmationDate>{pendingRescheduleData.displayDate}</ConfirmationDate>
            <ConfirmationShift>{pendingRescheduleData.displayShift}</ConfirmationShift>
            {isMatched && (
              <MatchingWarningContainer>
                <MatchingWarningText>
                  This appointment is currently matched to <strong>{matchedPeerName}</strong>.
                  Rescheduling will unmatch the peer by default.
                </MatchingWarningText>
                <KeepMatchingLabel>
                  <input
                    type="checkbox"
                    checked={keepMatching}
                    onChange={(e) => setKeepMatching(e.target.checked)}
                  />
                  <span>Keep current peer matching</span>
                </KeepMatchingLabel>
                {keepMatching && (
                  <KeepMatchingWarning>
                    Only keep matching if you have confirmed that {matchedPeerName} is still
                    available on the new date.
                  </KeepMatchingWarning>
                )}
              </MatchingWarningContainer>
            )}
            <ConfirmationButtons>
              <button className="button is-success" onClick={handleConfirmReschedule}>
                Confirm
              </button>
              <button className="button" onClick={() => setShowConfirmation(false)}>
                Cancel
              </button>
            </ConfirmationButtons>
          </ConfirmationContent>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Reschedule Task
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <CompactFormRow>
          <FormField>
            <FormLabel>Initials</FormLabel>
            <input
              type="text"
              className="input"
              value={initials || ""}
              onChange={(e) => setInitials(e.target.value)}
              explorator_test_id="to_do_list_reschedule_initials_input"
            />
          </FormField>
          <FormField style={{ flex: 2 }}>
            <FormLabel>Reason</FormLabel>
            <input
              type="text"
              className="input"
              value={reason || ""}
              onChange={(e) => setReason(e.target.value)}
              explorator_test_id="to_do_list_reschedule_reason_input"
            />
          </FormField>
          <SeenField>
            <SeenCheckbox seen={isSeen} setSeen={setIsSeen} />
          </SeenField>
        </CompactFormRow>

        <TabsContainer>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            <Tab label="Quick Move" />
            <Tab label="Custom Date & Time" />
          </Tabs>
        </TabsContainer>

        {activeTab === 0 && (
          <TabPanel>
            <TabDescription>Move the task forward by a number of shifts</TabDescription>
            <RadioGroup
              aria-labelledby="move-shift-forward-radio-group"
              name="move-shift-forward"
              onChange={(event) => setShiftNumForward(event.target.value)}
              value={shiftNumForward}
              explorator_test_id="to_do_list_reschedule_modal_move_forward_shift_selector"
            >
              <FormControlLabel value={1} control={<Radio />} label="One Shift" />
              <FormControlLabel value={2} control={<Radio />} label="Two Shifts" />
              <FormControlLabel value={3} control={<Radio />} label="Three Shifts" />
            </RadioGroup>
            <ActionButton
              className="button is-success"
              onClick={onConfirmMoveForward}
              explorator_test_id="to_do_list_reschedule_modal_move_forward_shift_submit_button"
            >
              Move Shift Forward
            </ActionButton>
          </TabPanel>
        )}

        {activeTab === 1 && (
          <TabPanel>
            <TabDescription>Choose a specific date and time</TabDescription>
            <DateInputContainer>
              <DateLabel>Date</DateLabel>
              <DateInput
                value={dateToRescheduleTo}
                onChange={(event) => setDateToRescheduleTo(event.target.value)}
                type="date"
                className="input"
              />
            </DateInputContainer>
            <RadioGroup
              aria-labelledby="custom-date-radio-group"
              defaultValue="1"
              name="custom-date-shift"
              onChange={(event) => setShiftNumCustom(event.target.value)}
              value={shiftNumCustom}
            >
              <FormControlLabel value="1" control={<Radio />} label="First Shift (7:00 AM)" />
              <FormControlLabel value="2" control={<Radio />} label="Second Shift (3:00 PM)" />
              <FormControlLabel value="3" control={<Radio />} label="Third Shift (11:00 PM)" />
              <FormControlLabel
                value="keep_original"
                control={<Radio />}
                label={<em>Keep original time</em>}
              />
              <FormControlLabel value="custom" control={<Radio />} label={<em>Custom time</em>} />
            </RadioGroup>
            {shiftNumCustom === "custom" && (
              <CustomTimeContainer>
                <TimeIcon className="far fa-clock" aria-hidden="true" />
                <TimeSelect value={customHour} onChange={(e) => setCustomHour(e.target.value)}>
                  {hourOptions.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </TimeSelect>
                <TimeSeparator>:</TimeSeparator>
                <TimeSelect value={customMinute} onChange={(e) => setCustomMinute(e.target.value)}>
                  {minuteOptions.map((minute) => (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  ))}
                </TimeSelect>
                <ShiftPreview>({shiftNumberToLabel(getShiftFromTime(customHour))})</ShiftPreview>
              </CustomTimeContainer>
            )}
            <ActionButton className="button is-success" onClick={onConfirmCustomDate}>
              Reschedule
            </ActionButton>
          </TabPanel>
        )}
      </DialogContent>
    </Dialog>
  );
};

TODOListActionButtonReScheduleModal.propTypes = {
  handleClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  modalOpen: PropTypes.bool,
  todoListId: PropTypes.string,
  shiftNumber: PropTypes.number,
  todoListDate: PropTypes.object,
  todoListTime: PropTypes.object,
  hasAppointmentRequest: PropTypes.bool,
  matchedPeerName: PropTypes.string
};

const CompactFormRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
  margin-bottom: 0.5rem;
  overflow: hidden;
`;

const FormField = styled.div`
  flex: 1;
  min-width: 0;

  input {
    width: 100%;
    box-sizing: border-box;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: #333;
`;

const SeenField = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 0.25rem;
  flex-shrink: 0;
`;

const TabsContainer = styled.div`
  margin-top: 0.75rem;
  border-bottom: 1px solid #e0e0e0;
`;

const TabPanel = styled.div`
  padding: 1rem 0;
`;

const TabDescription = styled.p`
  color: #666;
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const DateInputContainer = styled.div`
  margin-bottom: 1rem;
`;

const DateLabel = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const DateInput = styled.input`
  width: 100%;
  max-width: 200px;
`;

const ActionButton = styled.button`
  margin-top: 1.5rem;
`;

const ConfirmationContent = styled.div`
  text-align: center;
  padding: 1rem;
`;

const ConfirmationTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

const ConfirmationDate = styled.p`
  font-size: 1.2rem;
  font-weight: bold;
  margin: 0.5rem 0;
`;

const ConfirmationShift = styled.p`
  font-size: 1rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const MatchingWarningContainer = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  text-align: left;
`;

const MatchingWarningText = styled.p`
  font-size: 1rem;
  color: #856404;
  margin-bottom: 0.5rem;
`;

const KeepMatchingLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  color: #333;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
  }
`;

const KeepMatchingWarning = styled.p`
  font-size: 1rem;
  color: #d63384;
  font-style: italic;
  margin-top: 0.5rem;
  margin-bottom: 0;
`;

const CustomTimeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0 0.5rem 1.5rem;
  padding: 0.5rem;
  background-color: #f5f5f5;
  border-radius: 4px;
`;

const TimeIcon = styled.i`
  font-size: 1.2rem;
  color: #666;
`;

const TimeSelect = styled.select`
  padding: 0.3rem 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const TimeSeparator = styled.span`
  font-weight: bold;
  font-size: 1.2rem;
`;

const ShiftPreview = styled.span`
  font-size: 1rem;
  color: #666;
  margin-left: 0.5rem;
`;
